import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useDirtyState as useDirtyStateCore } from "@eudonet/opal";
/** Maps each stable error code to its i18n message key. */
const ERROR_MESSAGE_KEY = {
    "Contact.FirstName.Required": "contact.detail.identity.errors.firstNameRequired",
    "Contact.FirstName.TooLong": "contact.detail.identity.errors.firstNameTooLong",
    "Contact.LastName.Required": "contact.detail.identity.errors.lastNameRequired",
    "Contact.LastName.TooLong": "contact.detail.identity.errors.lastNameTooLong",
    "Contact.Courtesy.TooLong": "contact.detail.identity.errors.courtesyTooLong",
    "Contact.MaidenName.TooLong": "contact.detail.identity.errors.maidenNameTooLong",
    "Contact.BirthDate.InFuture": "contact.detail.identity.errors.birthDateInFuture",
    "Contact.PreferredLanguage.Invalid": "contact.detail.identity.errors.languageInvalid",
};
// Free-text length bounds mirrored from the OPHUB Contact entity (DB column +
// UpdateContactCommandValidator). Surfacing them inline on blur (OP-5650) gives
// the user an actionable error instead of a generic save-error toast after the
// backend rejects the over-length value.
const COURTESY_MAX_LENGTH = 50;
// FirstName / LastName / MaidenName all map to character varying(100).
const NAME_MAX_LENGTH = 100;
// BCP 47 (pragmatic subset): primary language subtag + optional subtags.
const BCP47 = /^[A-Za-z]{2,3}(-[A-Za-z0-9]{1,8})*$/;
/**
 * Identité bloc form logic (OP-5558 / OP-5862): local state, field-level
 * validation with stable error codes, and a `buildPayload()` fragment for the
 * page-level merged save (OP-5862 — Task 4 combines Identity + Profile into ONE
 * `updateContact()` call). Kept framework-light (no DOM) so it is unit-testable
 * in isolation.
 *
 * @param contact The loaded contact aggregate. The form re-populates and
 *   re-baselines whenever it resolves so freshly fetched data is not dirty.
 */
export function useContactIdentityForm(contact) {
    const { t } = useI18n();
    const form = reactive({
        civility: "",
        lastName: "",
        firstName: "",
        courtesy: "",
        maidenName: "",
        gender: "",
        birthDate: "",
        preferredLanguage: "",
    });
    // `enableBeforeUnload: false` — the page-level orchestrator owns the single
    // `beforeunload` listener covering every registered bloc.
    const { isDirty, markAsSaved, resetToOriginal } = useDirtyStateCore({
        data: form,
        enableBeforeUnload: false,
    });
    // Fields the user has left (blur). A field's error only surfaces once it is
    // touched, so a freshly loaded form never screams required errors.
    const touched = ref({});
    // Flipped by validate() (save attempt) to reveal every error at once,
    // regardless of which fields the user actually visited.
    const showAllErrors = ref(false);
    // Side-effect-free validity of the current values — the single source of truth
    // for both `isValid` (Save-button gating) and the surfaced `errorCodes`.
    const allErrorCodes = computed(() => {
        const next = {};
        // Required wins over length: an empty field reports "required", not "too long".
        if (!form.firstName.trim())
            next.firstName = "Contact.FirstName.Required";
        else if (form.firstName.trim().length > NAME_MAX_LENGTH)
            next.firstName = "Contact.FirstName.TooLong";
        if (!form.lastName.trim())
            next.lastName = "Contact.LastName.Required";
        else if (form.lastName.trim().length > NAME_MAX_LENGTH)
            next.lastName = "Contact.LastName.TooLong";
        // Courtesy / maiden name are optional free text but bounded by OPHUB —
        // surface the over-length inline rather than failing with a generic toast.
        if (form.courtesy.length > COURTESY_MAX_LENGTH)
            next.courtesy = "Contact.Courtesy.TooLong";
        if (form.maidenName.length > NAME_MAX_LENGTH)
            next.maidenName = "Contact.MaidenName.TooLong";
        // Birth date: ISO yyyy-MM-dd strings compare lexicographically, so a future
        // date is simply greater than today's string. Today is built from LOCAL date
        // parts (not toISOString, which is UTC and skews ±1 day off-UTC) so the guard
        // agrees with the user's calendar and with ageLabel's local-date parsing.
        if (form.birthDate) {
            const now = new Date();
            const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
            if (form.birthDate > todayIso) {
                next.birthDate = "Contact.BirthDate.InFuture";
            }
        }
        // Language comes from a fixed select, so an invalid value only arises from
        // malformed persisted data — validated as defense-in-depth and to mirror
        // the backend rules (BCP 47).
        if (form.preferredLanguage && !BCP47.test(form.preferredLanguage)) {
            next.preferredLanguage = "Contact.PreferredLanguage.Invalid";
        }
        return next;
    });
    const isValid = computed(() => Object.keys(allErrorCodes.value).length === 0);
    // Codes actually shown to the inputs: filtered to touched fields (or all after
    // a save attempt) so the user gets feedback on the field they just left
    // without the whole form lighting up before any interaction.
    const errorCodes = computed(() => {
        const out = {};
        for (const [field, code] of Object.entries(allErrorCodes.value)) {
            if (showAllErrors.value || touched.value[field])
                out[field] = code;
        }
        return out;
    });
    const errors = computed(() => {
        const out = {};
        for (const [field, code] of Object.entries(errorCodes.value)) {
            out[field] = t(ERROR_MESSAGE_KEY[code]);
        }
        return out;
    });
    function touch(field) {
        if (!touched.value[field])
            touched.value = { ...touched.value, [field]: true };
    }
    function validate() {
        // Save attempt — reveal every error regardless of which fields were visited.
        showAllErrors.value = true;
        return isValid.value;
    }
    /**
     * Returns the identity fragment of the merged PUT payload (OP-5862 — the page
     * issues ONE update covering Identity + Profile). Caller must validate() first.
     */
    function buildPayload() {
        return {
            salutation: (form.civility || undefined),
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            birthDate: form.birthDate || undefined,
            courtesy: form.courtesy || undefined,
            maidenName: form.maidenName || undefined,
            gender: form.gender || undefined,
            preferredLanguage: form.preferredLanguage || undefined,
        };
    }
    // Populate + re-baseline when the contact resolves or we switch to a different
    // contact. A *same-contact* re-fetch (e.g. another bloc's save() triggers a
    // page-level reload of the shared aggregate) MUST NOT clobber this bloc's
    // in-progress edits or wipe its inline validation errors. Re-initialise only
    // on a contact change, or when this bloc is clean (nothing unsaved to lose).
    let lastContactId = null;
    watch(contact, (loaded) => {
        if (!loaded)
            return;
        const sameContact = loaded.id === lastContactId;
        if (sameContact && (isDirty.value || Object.keys(errorCodes.value).length > 0)) {
            return;
        }
        lastContactId = loaded.id;
        form.civility = loaded.salutation ?? "";
        form.lastName = loaded.lastName;
        form.firstName = loaded.firstName;
        form.courtesy = loaded.courtesy ?? "";
        form.maidenName = loaded.maidenName ?? "";
        form.gender = loaded.gender ?? "";
        form.birthDate = loaded.birthDate ? loaded.birthDate.toISOString().slice(0, 10) : "";
        form.preferredLanguage = loaded.preferredLanguage ?? "";
        touched.value = {};
        showAllErrors.value = false;
        markAsSaved();
    }, { immediate: true });
    return {
        form,
        isDirty,
        isValid,
        errorCodes,
        errors,
        touch,
        validate,
        buildPayload,
        markAsSaved,
        resetToOriginal,
    };
}
